import React, { useState, useEffect } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import Button from '../common/Button';

const ChurchLogoSettings = () => {
  const [logoUrl, setLogoUrl] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    // Charger le logo depuis localStorage
    const savedLogo = localStorage.getItem('churchLogo');
    if (savedLogo) {
      setLogoUrl(savedLogo);
      setPreviewUrl(savedLogo);
    }
  }, []);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Vérifier le type de fichier
    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner une image (PNG, JPG, SVG)');
      return;
    }

    // Vérifier la taille (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('L\'image ne doit pas dépasser 2 MB');
      return;
    }

    setIsUploading(true);

    // Convertir en base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      setPreviewUrl(base64String);
      setIsUploading(false);
    };
    reader.onerror = () => {
      alert('Erreur lors de la lecture du fichier');
      setIsUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    setLogoUrl(url);
    setPreviewUrl(url);
  };

  const handleSave = () => {
    // Sauvegarder dans localStorage
    if (previewUrl) {
      localStorage.setItem('churchLogo', previewUrl);
      alert('Logo sauvegardé avec succès !');
    } else {
      localStorage.removeItem('churchLogo');
      alert('Logo supprimé avec succès !');
    }
  };

  const handleRemove = () => {
    setLogoUrl('');
    setPreviewUrl('');
    localStorage.removeItem('churchLogo');
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
        Logo de l'Église
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        Le logo apparaîtra sur les impressions des programmes. Formats acceptés : PNG, JPG, SVG (max 2 MB)
      </p>

      <div className="space-y-6">
        {/* Prévisualisation */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Prévisualisation
          </label>
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
            {previewUrl ? (
              <div className="relative inline-block">
                <img
                  src={previewUrl}
                  alt="Logo"
                  className="max-w-[200px] max-h-[200px] object-contain mx-auto"
                  onError={() => {
                    alert('Impossible de charger l\'image. Vérifiez l\'URL.');
                    setPreviewUrl('');
                  }}
                />
                <button
                  onClick={handleRemove}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  title="Supprimer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="text-gray-400">
                <ImageIcon className="w-16 h-16 mx-auto mb-2" />
                <p className="text-sm">Aucun logo</p>
              </div>
            )}
          </div>
        </div>

        {/* Upload de fichier */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Option 1 : Télécharger une image
          </label>
          <div className="flex items-center gap-3">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
              id="logo-upload"
            />
            <label
              htmlFor="logo-upload"
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer"
            >
              <Upload className="w-4 h-4" />
              {isUploading ? 'Chargement...' : 'Choisir une image'}
            </label>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              PNG, JPG, SVG (max 2 MB)
            </span>
          </div>
        </div>

        {/* URL d'image */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Option 2 : URL d'une image en ligne
          </label>
          <input
            type="url"
            value={logoUrl}
            onChange={handleUrlChange}
            placeholder="https://exemple.com/logo.png"
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Entrez l'URL complète d'une image hébergée en ligne
          </p>
        </div>

        {/* Boutons d'action */}
        <div className="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
          <Button
            variant="outline"
            onClick={handleRemove}
            disabled={!previewUrl}
          >
            Supprimer le logo
          </Button>
          <Button
            onClick={handleSave}
            disabled={!previewUrl}
          >
            Sauvegarder
          </Button>
        </div>
      </div>

      {/* Conseils */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">
          💡 Conseils pour un meilleur rendu
        </h4>
        <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
          <li>• Utilisez une image carrée (ex: 500x500 pixels)</li>
          <li>• Format PNG avec fond transparent recommandé</li>
          <li>• Le logo sera redimensionné à 80x80 pixels sur l'impression</li>
          <li>• Privilégiez un design simple et lisible</li>
        </ul>
      </div>
    </div>
  );
};

export default ChurchLogoSettings;
